import { useTranslation } from "react-i18next";
import clsx from "clsx";

type Tone = "ok" | "soon" | "due";

const TONE: Record<Tone, string> = {
  ok: "border-green-400/40 text-green-400",
  soon: "border-amber-400/40 text-amber-400",
  due: "border-orange-400/50 bg-orange-400/10 text-orange-400",
};

export default function FleetMock() {
  const { t, i18n } = useTranslation("pages");
  const date = (iso: string) =>
    new Date(iso).toLocaleDateString(i18n.language, {
      month: "short",
      day: "numeric",
    });

  // Illustrative roster: real-looking consumable states, not real customers.
  const vehicles = [
    {
      num: "49",
      name: "Hyundai Elantra N",
      line: t("home.mock.fleet.line", {
        driver: t("home.mock.fleet.driver1"),
        km: "2,410",
        date: `${date("2026-10-04")}, Everland`,
      }),
      pills: [
        { tone: "due" as Tone, text: t("home.mock.fleet.padsDue") },
        { tone: "ok" as Tone, text: t("home.mock.fleet.oilKm", { km: 640 }) },
      ],
    },
    {
      num: "7",
      name: "Toyota GR86",
      line: t("home.mock.fleet.line", {
        driver: t("home.mock.fleet.driver2"),
        km: "1,180",
        date: `${date("2026-09-27")}, Inje`,
      }),
      pills: [
        { tone: "soon" as Tone, text: t("home.mock.fleet.tiresKm", { km: 120 }) },
        { tone: "ok" as Tone, text: t("home.mock.fleet.oilOk") },
      ],
    },
    {
      num: "21",
      name: "BMW M2",
      line: t("home.mock.fleet.line", {
        driver: t("home.mock.fleet.driver3"),
        km: "3,905",
        date: `${date("2026-10-04")}, Everland`,
      }),
      pills: [{ tone: "ok" as Tone, text: t("home.mock.fleet.allOk") }],
    },
  ];

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-800 bg-gray-900/60">
      {vehicles.map((v) => (
        <div
          key={v.num}
          className="grid grid-cols-[auto_1fr] items-center gap-3.5 border-b border-gray-800 px-4 py-3.5 last:border-b-0 sm:grid-cols-[auto_1fr_auto]"
        >
          <span className="grid h-9 min-w-9 place-items-center rounded-lg bg-white px-1 font-mono text-[13px] font-semibold text-gray-900">
            {v.num}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white">{v.name}</div>
            <div className="mt-0.5 text-xs text-gray-400">{v.line}</div>
          </div>
          <div className="col-span-2 flex flex-wrap gap-1.5 sm:col-span-1 sm:justify-end">
            {v.pills.map((p) => (
              <span
                key={p.text}
                className={clsx(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[10.5px] tracking-[0.02em]",
                  TONE[p.tone],
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                {p.text}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
